import {
	MapHelperInstance,
	useStyleDependantMapEffect,
	useStyleSetupMapEffect,
} from "../../helper/map-helper";
import { VelavuGeofence } from "velavu-js-api";
import { geoJSONBlankCollection } from "./mapbox-injectable";
import {
	ExpressionSpecification,
	GeoJSONSource,
	MapMouseEvent,
} from "mapbox-gl";
import { useListenerSetupMapEffect } from "../../helper/map-helper";
import { useCallback, useRef } from "react";
import { isMapMouseEventOnLayer } from "../../helper/mapbox-helper";
import whitelabel from "../../whitelabel";

const mapboxIDGeofencesSource = "geofence-source";
const mapboxIDGeofencesFill = "geofence-fill";
const mapboxIDGeofencesLine = "geofence-line";
const mapboxIDGeofencesCenterSource = "geofence-center-source";
const mapboxIDGeofencesLabel = "geofence-label";

const visualLayers: readonly string[] = [
	mapboxIDGeofencesFill,
	mapboxIDGeofencesLine,
	mapboxIDGeofencesLabel,
];

const featureStateHover = "hover";
const featureStateSelected = "selected";

const expressionHighlight: ExpressionSpecification = [
	"any",
	["boolean", ["feature-state", featureStateSelected], false],
	["boolean", ["feature-state", featureStateHover], false],
];

export interface MapboxInjectableGeofencesParams {
	mapInstance: MapHelperInstance;
	geofences: VelavuGeofence[];
	selectable?: boolean;
	selectedGeofenceID?: string;
	hoveredGeofenceID?: string;
	onHoverGeofence?: (geofenceID: string | undefined) => void;
	onSelectGeofence?: (geofenceID: string) => void;
	belowLayer?: string;
}

export default function useMapboxInjectableGeofences(
	params: MapboxInjectableGeofencesParams,
): string {
	// Maintain a mapping of geofence IDs to numeric IDs.
	// Since Mapbox only accepts numbers as feature IDs, we
	// need to map our string IDs to numbers.
	const nextGeofenceNumericID = useRef(0);
	const geofenceIDToNumericIDMapping = useRef(
		new Map<string, number>(),
	).current;
	const numericIDToGeofenceIDMapping = useRef(
		new Map<number, string>(),
	).current;
	const requireGeofenceNumericID = useCallback(
		(geofenceID: string) => {
			if (!geofenceIDToNumericIDMapping.has(geofenceID)) {
				const numericID = nextGeofenceNumericID.current;
				nextGeofenceNumericID.current += 1;

				geofenceIDToNumericIDMapping.set(geofenceID, numericID);
				numericIDToGeofenceIDMapping.set(numericID, geofenceID);
				return numericID;
			} else {
				return geofenceIDToNumericIDMapping.get(geofenceID)!;
			}
		},
		[geofenceIDToNumericIDMapping, numericIDToGeofenceIDMapping],
	);

	useStyleSetupMapEffect(params.mapInstance, (_, box) => {
		box.addSource(mapboxIDGeofencesSource, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});
		box.addSource(mapboxIDGeofencesCenterSource, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});

		box.addLayer(
			{
				id: mapboxIDGeofencesLabel,
				type: "symbol",
				source: mapboxIDGeofencesCenterSource,
				layout: {
					"text-field": ["get", "name"],
					"text-size": 12,
					"text-font": [
						"Poppins",
						"Open Sans Regular",
						"Arial Unicode MS Regular",
					],
				},
				paint: {
					"text-color": [
						"case",
						expressionHighlight,
						whitelabel.palette.primary900,
						whitelabel.palette.secondary700,
					],
					"text-halo-color": whitelabel.palette.secondary50,
					"text-halo-width": 1,
				},
			},
			params.belowLayer,
		);
		box.addLayer(
			{
				id: mapboxIDGeofencesLine,
				type: "line",
				source: mapboxIDGeofencesSource,
				paint: {
					"line-color": [
						"case",
						expressionHighlight,
						whitelabel.palette.primary900,
						whitelabel.palette.secondary500,
					],
					"line-width": 2,
				},
			},
			mapboxIDGeofencesLabel,
		);
		box.addLayer(
			{
				id: mapboxIDGeofencesFill,
				type: "fill",
				source: mapboxIDGeofencesSource,
				paint: {
					"fill-color": [
						"case",
						expressionHighlight,
						whitelabel.palette.primary900,
						whitelabel.palette.secondary500,
					],
					"fill-opacity": ["case", expressionHighlight, 0.15, 0.3],
				},
			},
			mapboxIDGeofencesLine,
		);
	});

	//Update geofence polygon data
	useStyleDependantMapEffect(
		params.mapInstance,
		(map, _) => {
			const geofencesSource = map.getSource(
				mapboxIDGeofencesSource,
			) as GeoJSONSource;

			geofencesSource.setData({
				type: "FeatureCollection",
				features: params.geofences.map((geofence) => ({
					type: "Feature",
					id: requireGeofenceNumericID(geofence.id),
					geometry: geofence.boundary,
					properties: {},
				})),
			});
		},
		[params.geofences, requireGeofenceNumericID],
	);

	//Update geofence polygon center data
	useStyleDependantMapEffect(
		params.mapInstance,
		(map, _) => {
			const geofencesCenterSource = map.getSource(
				mapboxIDGeofencesCenterSource,
			) as GeoJSONSource;

			geofencesCenterSource.setData({
				type: "FeatureCollection",
				features: params.geofences.map((geofence) => ({
					type: "Feature",
					id: requireGeofenceNumericID(geofence.id),
					geometry: {
						type: "Point",
						coordinates: geofence.center,
					},
					properties: {
						name: geofence.name,
					},
				})),
			});
		},
		[params.geofences, requireGeofenceNumericID],
	);

	// Update selected feature state based on selected geofence
	useStyleDependantMapEffect(
		params.mapInstance,
		(map, _) => {
			if (params.selectedGeofenceID === undefined) return;

			const geofenceNumericID = geofenceIDToNumericIDMapping.get(
				params.selectedGeofenceID,
			);
			if (geofenceNumericID === undefined) return;

			const sourceArray = [
				mapboxIDGeofencesSource,
				mapboxIDGeofencesCenterSource,
			];

			for (const source of sourceArray) {
				map.setFeatureState(
					{
						source: source,
						id: geofenceNumericID,
					},
					{
						[featureStateSelected]: true,
					},
				);
			}

			return () => {
				for (const source of sourceArray) {
					map.setFeatureState(
						{
							source: source,
							id: geofenceNumericID,
						},
						{
							[featureStateSelected]: false,
						},
					);
				}
			};
		},
		[params.selectedGeofenceID],
	);

	//Update highlight feature state based on hovered geofence
	useListenerSetupMapEffect(
		params.mapInstance.map,
		(map) => {
			if (!params.selectable || params.hoveredGeofenceID === undefined) {
				return;
			}
			const geofenceNumericID = geofenceIDToNumericIDMapping.get(
				params.hoveredGeofenceID,
			);
			if (geofenceNumericID === undefined) {
				return;
			}

			const sourceArray = [
				mapboxIDGeofencesSource,
				mapboxIDGeofencesCenterSource,
			];

			for (const source of sourceArray) {
				map.setFeatureState(
					{
						source: source,
						id: geofenceNumericID,
					},
					{
						[featureStateHover]: true,
					},
				);
			}

			return () => {
				for (const source of sourceArray) {
					map.setFeatureState(
						{
							source: source,
							id: geofenceNumericID,
						},
						{
							[featureStateHover]: false,
						},
					);
				}
			};
		},
		[params.selectable, params.hoveredGeofenceID],
	);

	//Add map hover callbacks
	useListenerSetupMapEffect(
		params.mapInstance.map,
		(_, box) => {
			const onHoverGeofence = params.onHoverGeofence;
			if (!params.selectable || onHoverGeofence === undefined) return;

			box.on("mousemove", mapboxIDGeofencesFill, (event) => {
				//Make sure we are the topmost layer
				if (
					!isMapMouseEventOnLayer(
						event,
						mapboxIDGeofencesFill,
						visualLayers,
					)
				) {
					onHoverGeofence(undefined);
					return;
				}

				if (event.features !== undefined && event.features.length > 0) {
					const feature = event.features[0];
					const geofenceNumericID = feature.id as number;
					const geofenceID =
						numericIDToGeofenceIDMapping.get(geofenceNumericID)!;

					onHoverGeofence(geofenceID);
				}
			});

			const onMouseLeave = () => {
				onHoverGeofence(undefined);
			};
			box.on("mouseleave", mapboxIDGeofencesFill, onMouseLeave);
			return onMouseLeave;
		},
		[params.selectable, params.onHoverGeofence],
	);

	//Add map click callback
	useListenerSetupMapEffect(
		params.mapInstance.map,
		(_, box) => {
			const onSelectGeofence = params.onSelectGeofence;
			if (!params.selectable || onSelectGeofence === undefined) return;

			box.on("click", mapboxIDGeofencesFill, (event) => {
				//Make sure we are the topmost layer
				if (
					!isMapMouseEventOnLayer(
						event,
						mapboxIDGeofencesFill,
						visualLayers,
					)
				) {
					return;
				}

				if (event.features !== undefined && event.features.length > 0) {
					const feature = event.features[0];
					const geofenceNumericID = feature.id as number;
					const geofenceID =
						numericIDToGeofenceIDMapping.get(geofenceNumericID)!;

					onSelectGeofence(geofenceID);
				}
			});
		},
		[params.selectable, params.onSelectGeofence],
	);

	return mapboxIDGeofencesFill;
}
